/**
 * DoserneChat — full-page internal Doserne Academy chat.
 *
 * Two-column desktop layout (rooms sidebar + open room). On mobile the
 * two views swap in place. All reusable pieces (RoomView, MessageBubble,
 * NewRoomModal, MarkToProjectModal, helpers) live in `ChatRoomView.jsx`
 * so the floating Messenger windows in `ChatFab.jsx` can reuse them.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2, MessageCircle, Plus, Users, BellOff, Search } from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";
import { RoomView, NewRoomModal, isOnline, otherMember, timeAgo } from "./ChatRoomView";
import ChatSearchModal from "./ChatSearchModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------
// ROOM LIST (sidebar)
// -----------------------------------------------------------------
function RoomList({ rooms, currentRoomId, onSelect, myEmail, onNew, onSearch, colleagueLabels }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-2">
        <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Samtaler</h2>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onSearch}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
            title="Søk i alle samtaler"
            data-testid="chat-search-btn"
          >
            <Search className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onNew}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-black"
            style={{ color: "#fff" }}
            data-testid="chat-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Ny
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {rooms.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-400 italic">Ingen samtaler ennå.<br />Trykk «Ny» for å starte.</div>
        ) : (
          rooms.map((r) => {
            const other = otherMember(r, myEmail);
            const label = r.type === "dm" ? (colleagueLabels[other] || other) : (r.name || (r.type === "project" ? "Prosjekt" : "Gruppe"));
            const preview = r.last_message
              ? (r.last_message.audio_url ? "🎙️ Talenotat" : r.last_message.photo_url ? "📷 Bilde" : (r.last_message.body || ""))
              : "Ingen meldinger ennå";
            const active = r.id === currentRoomId;
            return (
              <button
                key={r.id}
                onClick={() => onSelect(r)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 flex items-center gap-3 ${active ? "bg-emerald-50" : ""}`}
                data-testid={`chat-room-${r.id}`}
              >
                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${r.type === "group" ? "bg-amber-100 text-amber-700" : r.type === "project" ? "bg-cyan-100 text-cyan-700" : "bg-slate-200 text-slate-600"}`}>
                  {r.type === "group" || r.type === "project" ? <Users className="w-5 h-5" /> : (label || "?").slice(0, 1).toUpperCase()}
                  {r.type === "dm" && isOnline(r.other_last_seen_at) && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" title="Online" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-black text-slate-900 truncate flex items-center gap-1">
                      {label}
                      {r.muted && <BellOff className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                    </div>
                    {r.last_message?.created_at && <div className="text-[10px] text-slate-400 flex-shrink-0">{timeAgo(r.last_message.created_at)}</div>}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-500 truncate">{preview}</div>
                    {r.unread > 0 && (
                      <div className="text-[10px] bg-emerald-600 rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 font-black flex-shrink-0" style={{ color: "#fff" }}>
                        {r.unread}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// MAIN PAGE
// -----------------------------------------------------------------
export default function DoserneChat() {
  const { user, loading, authHeader } = useDoserneAuth();
  const [rooms, setRooms] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadRooms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/chat/rooms`, authHeader());
      setRooms(data.rooms || []);
      return data.rooms || [];
    } catch { return []; }
  }, [authHeader]);

  const loadColleagues = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/colleagues`, authHeader());
      setColleagues((data.colleagues || []).filter((c) => !c.is_me));
    } catch (_e) { /* ignore */ void 0; }
  }, [authHeader]);

  useEffect(() => {
    if (!user) return;
    loadRooms().then((rs) => {
      const openId = searchParams.get("room");
      if (openId) {
        const found = rs.find((r) => r.id === openId);
        if (found) setCurrentRoom(found);
      }
    });
    loadColleagues();
    const iv = setInterval(loadRooms, 15000);
    return () => clearInterval(iv);
  }, [user, loadRooms, loadColleagues, searchParams]);

  const colleagueLabels = useMemo(() => {
    const map = {};
    for (const c of colleagues) map[c.email] = c.full_name || c.email;
    if (user) map[user.email] = user.full_name || user.email;
    return map;
  }, [colleagues, user]);

  const openRoom = (r) => {
    setCurrentRoom(r);
    setSearchParams({ room: r.id });
    setRooms((cur) => cur.map((x) => x.id === r.id ? { ...x, unread: 0 } : x));
  };

  // ---------------------------------------------------------------
  // MOBILE KEYBOARD LAYOUT FIX
  // When a chat room is open on a small screen we (a) lock the page
  // scroll and (b) pin the chat container to the viewport with
  // `position: fixed` below the sticky 64px header. Both are needed
  // so iOS Safari doesn't scroll the whole document up when the
  // virtual keyboard opens on the composer input.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!currentRoom) return undefined;
    const isMobile = window.matchMedia("(max-width: 1023px)").matches;
    if (!isMobile) return undefined;
    const origBody = document.body.style.overflow;
    const origHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = origBody;
      document.documentElement.style.overflow = origHtml;
    };
  }, [currentRoom]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  return (
    <DoserneShell>
      {/* On mobile the heading is hidden while a chat room is open —
          Messenger-style — so the chat panel can eat the whole
          viewport minus the sticky header. Desktop still shows the
          nice "Meldinger" title above the two-column layout. */}
      <div className={`mb-4 ${currentRoom ? "hidden lg:block" : ""}`}>
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">Chat</div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Meldinger</h1>
      </div>

      {/* Height math:
            desktop  → 100dvh - 140px (page chrome + heading)
            mobile   → When a room IS open we pin the container with
                       `fixed inset-x-0 top-16 bottom-0` so the iOS
                       virtual keyboard can't scroll the whole
                       document up (root fix for the "screen pushed
                       too high" bug). Combined with the body-lock
                       effect above, only the chat's internal scroll
                       area moves. When NO room is open we keep the
                       normal in-flow layout so the room list can
                       scroll with the page. */}
      <div
        className={`bg-white border-slate-200 shadow-sm lg:border lg:rounded-2xl lg:overflow-hidden lg:h-[calc(100dvh-140px)] lg:min-h-[500px] lg:max-h-[900px] ${
          currentRoom
            ? "max-lg:fixed max-lg:inset-x-0 max-lg:top-16 max-lg:bottom-0 max-lg:z-30 max-lg:bg-white max-lg:overflow-hidden"
            : "max-lg:border max-lg:rounded-2xl max-lg:overflow-hidden max-lg:h-[calc(100dvh-200px)] max-lg:min-h-[400px]"
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] h-full min-h-0">
          <div className={`border-r border-slate-200 ${currentRoom ? "hidden lg:flex" : "flex"} flex-col min-h-0`}>
            <RoomList
              rooms={rooms}
              currentRoomId={currentRoom?.id}
              onSelect={openRoom}
              myEmail={user.email}
              onNew={() => setShowNew(true)}
              onSearch={() => setShowSearch(true)}
              colleagueLabels={colleagueLabels}
            />
          </div>
          <div className={`${currentRoom ? "flex" : "hidden lg:flex"} flex-col min-h-0`}>
            {currentRoom ? (
              <RoomView
                key={currentRoom.id}
                room={currentRoom}
                myEmail={user.email}
                authHeader={authHeader}
                onBack={() => { setCurrentRoom(null); setSearchParams({}); }}
                colleagueLabels={colleagueLabels}
                onMuteToggled={(rid, m) => setRooms((cur) => cur.map((x) => x.id === rid ? { ...x, muted: m } : x))}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <div>Velg en samtale eller start en ny</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showNew && (
        <NewRoomModal
          onClose={() => setShowNew(false)}
          authHeader={authHeader}
          colleagues={colleagues}
          myEmail={user.email}
          onCreated={(r) => { setRooms((cur) => [r, ...cur.filter((x) => x.id !== r.id)]); openRoom(r); }}
        />
      )}

      <ChatSearchModal
        open={showSearch}
        onClose={() => setShowSearch(false)}
        authHeader={authHeader}
        onOpenRoom={(roomId) => {
          const r = rooms.find((x) => x.id === roomId);
          if (r) openRoom(r);
        }}
      />
    </DoserneShell>
  );
}
